import { useMemo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Box, Stack } from "@mui/material"
import fp from "lodash/fp"

import type { Tower } from "types/towers.types"

import PageTitle from "widgets/common/PageTitle"
import EntityTag from "widgets/common/EntityTag"
import AssetContextBlock from "widgets/common/AssetContextBlock"
import MetaItem, { MetaList } from "widgets/common/MetaItem"
import { formatLength } from "helpers/utils/powerlines"
import { nameOrUnnamed } from "helpers/utils/translations"
import { getSpanDisplayName } from "helpers/utils/spans"
import useContextDevices from "helpers/hooks/devices/useContextDevices"
import { useAllPowerlines } from "helpers/hooks/powerlines/useAllPowerlines"
import { useTowerSpans } from "helpers/hooks/spans/useTowerSpans"

interface TowerSummaryProps {
  tower: Tower
  /** The page's controls, such as the date range. */
  actions?: ReactNode
}

const TowerSummary = ({ tower, actions }: TowerSummaryProps) => {
  const { i18n, t } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  const {
    powerlinesById,
    powerlineIdsByTowerId,
    isLoading: powerlinesLoading,
  } = useAllPowerlines()
  const { devices, isLoading: devicesLoading } = useContextDevices({
    asset: { entity: "tower", id: tower.id },
  })

  // The tower does not name its lines, so they come from the lines that list it.
  const powerlines = useMemo(
    () =>
      fp.compact((powerlineIdsByTowerId[tower.id] ?? []).map((id) => powerlinesById[id])),
    [powerlineIdsByTowerId, powerlinesById, tower.id],
  )
  const { spans: towerSpans, isLoading: spansLoading } = useTowerSpans({
    towerId: tower.id,
    powerlineIds: powerlines.map((powerline) => powerline.id),
  })
  // Each line with its own spans, each span named by its position on that line.
  const linesWithSpans = useMemo(
    () =>
      powerlines.map((powerline) => ({
        id: powerline.id,
        name: powerline.name,
        spans: towerSpans
          .filter((span) => span.powerline_id === powerline.id)
          .map((span) => ({
            id: span.id,
            name: getSpanDisplayName(span, powerline.span_ids, t),
          })),
      })),
    [powerlines, towerSpans, t],
  )

  return (
    <Box
      sx={{
        marginBottom: "1rem",
      }}
      data-testid="tower-summary"
    >
      <Stack
        flexDirection="row"
        flexWrap="wrap"
        columnGap={2}
        rowGap={1}
        alignItems="center"
        marginBottom={1.5}
      >
        <EntityTag entity="tower" />
        <PageTitle
          text={nameOrUnnamed(tower.name, t)}
          styles={{ fontStyle: tower.name ? "normal" : "italic" }}
        />
        {/* The end of the title row on desktop, a full-width row of its own on mobile. */}
        {actions && (
          <Box sx={{ marginLeft: { md: "auto" }, width: { xs: "100%", md: "auto" } }}>
            {actions}
          </Box>
        )}
      </Stack>
      <Stack gap={1.5}>
        <MetaList>
          <MetaItem label="ID">{tower.id}</MetaItem>
          {!fp.isNil(tower.type) && (
            <MetaItem label={t("tower_information.TYPE")}>
              {t(`tower_information.TYPES.${tower.type.toUpperCase()}`, tower.type)}
            </MetaItem>
          )}
          {!fp.isNil(tower.height) && (
            <MetaItem label={t("tower_information.HEIGHT")}>
              {formatLength(tower.height, locale)}
            </MetaItem>
          )}
        </MetaList>
        <AssetContextBlock
          powerLines={linesWithSpans}
          devices={{ items: devices }}
          loading={{
            powerLines: powerlinesLoading,
            spans: spansLoading,
            devices: devicesLoading,
          }}
        />
      </Stack>
    </Box>
  )
}

export default TowerSummary
